import { StaffTracking } from '../types';
import { KitchenPlan } from './plan';
import { zoneForStation } from './stations';

export interface StationSummary {
  station: string;
  zoneId: string | null;
  /** Workers on station at the last interval the job wrote. */
  avgWorkers: number;
  totalWorkTimeSec: number;
  mealsProcessed: number;
  mealsToProcess: number;
}

export interface ShiftSummary {
  day: string;
  shift: string;
  intervalsDone: number;
  grandTotalWorkTimeSec: number;
  stations: StationSummary[];
  /** Stations the job reported that no zone on this plan matches. */
  unmatchedStations: string[];
}

/** The last interval of a StaffTracking record, folded per station. */
export const readStaffTracking = (kitchenPlan: KitchenPlan, record: StaffTracking): ShiftSummary | null => {
  const intervals = (record.intervals ?? []).filter(Boolean);
  const latest = intervals.at(-1);
  if (!latest) {
    return null;
  }

  const byStation = new Map<string, StationSummary>();
  const take = (station: string): StationSummary => {
    let entry = byStation.get(station);
    if (!entry) {
      entry = {
        station,
        zoneId: zoneForStation(kitchenPlan.zoneById, station),
        avgWorkers: 0,
        totalWorkTimeSec: 0,
        mealsProcessed: 0,
        mealsToProcess: 0
      };
      byStation.set(station, entry);
    }
    return entry;
  };

  for (const value of latest?.latestAvgWorkersPerStation ?? []) {
    if (value) {
      take(value.station).avgWorkers = value.value;
    }
  }
  for (const value of latest?.totalWorkTimeSecPerStation ?? []) {
    if (value) {
      take(value.station).totalWorkTimeSec = value.value;
    }
  }
  for (const perStation of latest?.perStationMeals ?? []) {
    if (!perStation) {
      continue;
    }
    const entry = take(perStation.station);
    for (const meal of perStation.meals ?? []) {
      entry.mealsProcessed += meal?.mealsProcessed ?? 0;
      entry.mealsToProcess += meal?.totalMealsToProcess ?? 0;
    }
  }

  const stations = [...byStation.values()].sort((a, b) => b.avgWorkers - a.avgWorkers);

  return {
    day: record.day,
    shift: record.shift,
    intervalsDone: latest?.intervalsDone ?? 0,
    grandTotalWorkTimeSec: latest?.grandTotalWorkTimeSec ?? 0,
    stations,
    unmatchedStations: stations.filter((entry) => !entry.zoneId).map((entry) => entry.station)
  };
};
